import { supabase } from "@/integrations/supabase/client";
import { fetchAllRows } from "@/lib/paginate";

export type ProjectSummary = {
  slug: string;
  name: string;
  units: number;
  minPrice: number | null;
  currency: string;
  minArea: number | null;
  maxArea: number | null;
  types: string[];
};

export function slugifyProject(name: string) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Agrupa las propiedades publicadas por edificio/proyecto (address_text). */
export async function fetchProjects(): Promise<ProjectSummary[]> {
  const rows = await fetchAllRows((from, to) =>
    supabase
      .from("properties")
      .select("address_text, price_amount, price_currency, area_m2, property_type")
      .neq("status", "borrador")
      .not("address_text", "is", null)
      .range(from, to),
  );

  const map = new Map<string, ProjectSummary>();
  for (const r of rows as Array<{
    address_text: string | null;
    price_amount: number | null;
    price_currency: string | null;
    area_m2: number | null;
    property_type: string | null;
  }>) {
    const name = (r.address_text ?? "").trim();
    if (!name) continue;
    const slug = slugifyProject(name);
    const cur = map.get(slug) ?? {
      slug,
      name,
      units: 0,
      minPrice: null,
      currency: r.price_currency ?? "USD",
      minArea: null,
      maxArea: null,
      types: [],
    };
    cur.units += 1;
    const price = Number(r.price_amount ?? 0);
    if (price > 0 && (cur.minPrice === null || price < cur.minPrice)) {
      cur.minPrice = price;
      cur.currency = r.price_currency ?? cur.currency;
    }
    const area = r.area_m2 === null ? null : Number(r.area_m2);
    if (area && area > 0) {
      cur.minArea = cur.minArea === null ? area : Math.min(cur.minArea, area);
      cur.maxArea = cur.maxArea === null ? area : Math.max(cur.maxArea, area);
    }
    if (r.property_type && !cur.types.includes(r.property_type)) cur.types.push(r.property_type);
    map.set(slug, cur);
  }

  return [...map.values()].sort((a, b) => b.units - a.units);
}

export async function findProjectBySlug(slug: string) {
  const list = await fetchProjects();
  return list.find((p) => p.slug === slug) ?? null;
}
